import React, { useMemo, useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import SearchMenu from "../../components/SearchMenuConfigSync/SearchMenuConfigSync.jsx";
import CardGraph from "../../components/CardGraph/CardGraph";
import { getColorArray } from "../../helpers/getColorGraphs";
import { convertToMapById } from "../../helpers/convertToMap";
import { useColorTheme } from "../../hooks/useColorTheme";
import { useTimer } from "../../hooks/useTimer";
import {
  getData,
  getUnits,
  changeStation,
} from "../../redux/ducks/ordersGraph";
import { configGraphTime, configGraphTimeDay } from "./config";
import "./styles.css";

const options = {
  maintainAspectRatio: true,
  legend: {
    align: "start",
  },
  lineAtIndex: 2,
  scales: {
    xAxes: [
      {
        position: "top",
        ticks: {
          beginAtZero: true,
        },
      },
    ],
  },
  tooltips: {
    displayColors: false,
    mode: "index",
    callbacks: {
      beforeLabel: function(tooltipItem, data) {
        const label = data.units[tooltipItem.label];
        return `Promedio: ${label ? label.day_average : "0"} hrs`;
      },
      label: function(tooltipItem, data) {
        const label = data.units[tooltipItem.label];
        return `Max: ${label ? label.max : "0"} hrs`;
      },
      afterLabel: function(tooltipItem, data) {
        const label = data.units[tooltipItem.label];
        return `Min: ${label ? label.min : "0"} hrs`;
      },
    },
  },
};

const optionsTime = {
  maintainAspectRatio: true,
  legend: {
    align: "start",
  },
  lineAtIndex: 2,
  scales: {
    xAxes: [
      {
        position: "top",
        ticks: {
          beginAtZero: true,
        },
      },
    ],
  },
  tooltips: {
    displayColors: false,
    mode: "index",
    callbacks: {
      beforeLabel: function(tooltipItem) {
        const label = "Tiempo total: " + tooltipItem.value + " hrs";
        return label;
      },
      label: function(tooltipItem, data) {
        const label = data.units[tooltipItem.label];
        return `Servicios: ${label ? label.services : "0"}`;
      },
      afterLabel: function(tooltipItem, data) {
        const label = data.units[tooltipItem.label];
        return `Promedio de entrega: ${label ? label.hist_number : "0"} hrs`;
      },
    },
  },
};

const OrdersGraph = () => {
  const dispatch = useDispatch();
  const colorBg = useSelector((state) => state.config.colorBg);
  const { companies } = useSelector((state) => state.companies);
  const stationSelected = useSelector(
    (state) => state.ordersGraph.stationSelected
  );
  const units = useSelector((state) => state.ordersGraph.units);
  const unitsTimeAxeX = useSelector((state) => state.ordersGraph.unitsTimeAxeX);
  const unitsTimeAxeY = useSelector((state) => state.ordersGraph.unitsTimeAxeY);
  const unitsTimeDayAxeX = useSelector(
    (state) => state.ordersGraph.unitsTimeDayAxeX
  );
  const unitsTimeDayAxeY = useSelector(
    (state) => state.ordersGraph.unitsTimeDayAxeY
  );

  const [mapCompanies, setMapCompanies] = useState([]);
  const [selectCompany, setSelectCompany] = useState([]);
  const [stationsMap, setStationsMap] = useState([]);
  const { color, colorRGB } = useColorTheme(colorBg);
  const [runTimer] = useTimer(1000);

  const handleCompany = ({ target }) => {
    const { value } = target;
    setSelectCompany(value);
    setStationsMap(companies[value].stations);
  };

  useEffect(() => {
    setMapCompanies(convertToMapById(companies));
  }, [companies]);

  const getDataGraphTime = () => {
    return {
      labels: unitsTimeAxeY,
      datasets: [
        {
          label: "Unidades",
          data: unitsTimeAxeX,
          backgroundColor: getColorArray(unitsTimeAxeX, configGraphTime),
        },
      ],
      units: units,
    };
  };

  const getDataGraphTimeDay = () => {
    return {
      labels: unitsTimeDayAxeY,
      datasets: [
        {
          label: "Unidades",
          data: unitsTimeDayAxeX,
          backgroundColor: getColorArray(unitsTimeDayAxeX, configGraphTimeDay),
        },
      ],
      units: units,
    };
  };

  const data = useMemo(
    () => getDataGraphTime(),
    [unitsTimeAxeY, unitsTimeAxeX, units]
  );
  const dataDay = useMemo(
    () => getDataGraphTimeDay(),
    [unitsTimeDayAxeY, unitsTimeDayAxeX, units]
  );

  const fetchData = useCallback(() => {
    dispatch(getData());
  }, [dispatch]);

  const fetchUnits = useCallback(
    (stations) => {
      dispatch(getUnits(stations));
    },
    [dispatch]
  );

  const changeStationMemoized = useCallback(
    (station) => {
      dispatch(changeStation(station));
    },
    [dispatch]
  );

  const handleStationSelect = (event) => {
    changeStationMemoized(event.target.value);
    if (event.target.value) {
      runTimer(fetchUnits, [[{ value: event.target.value }]]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div>
      <SearchMenu
        color={color}
        colorRGB={colorRGB}
        companies={mapCompanies}
        stations={stationsMap}
        companySelect={selectCompany}
        stationSelect={stationSelected}
        onCompanySelect={handleCompany}
        onStationSelect={handleStationSelect}
      />
      <GridContainer>
        <GridItem xs={12} sm={12} md={6}>
          <CardGraph
            title="Tiempo de entrega promedio X total de pedidos en el dia"
            height={500}
            data={data}
            options={optionsTime}
          />
        </GridItem>
        <GridItem xs={12} sm={12} md={6}>
          <CardGraph
            title="Tiempo de entrega promedio en el dia"
            height={500}
            data={dataDay}
            options={options}
          />
        </GridItem>
      </GridContainer>
    </div>
  );
};

export default OrdersGraph;
